import { Component, useCallback, useEffect, useState, type ErrorInfo, type ReactNode } from 'react';
import type { SessionResponse } from '@movienight/shared';
import scriptSource from '@script/vrata.yaml?raw';
import { Door } from '../components/Door';
import { BouncerScene } from './BouncerScene';
import { FallbackForm } from './FallbackForm';
import { loadScript, type Script } from './dialogue';
import { unlock } from './audio';
import { getSession } from '../lib/api';

/**
 * Orchestrates the whole entrance: the physical door, the scene behind it, and
 * the plain form when anything goes wrong.
 *
 * The script is parsed **once, eagerly**, before the door is openable. If it
 * is bad we know immediately and route to the fallback — rather than letting
 * someone knock, watch the door swing, and then hit a dead scene.
 */

type Stage =
  | { at: 'door' }
  | { at: 'scene' }
  | { at: 'fallback'; reason: string };

export function DoorFlow({ onEntered }: { onEntered: () => void }) {
  const [stage, setStage] = useState<Stage>({ at: 'door' });
  const [session, setSession] = useState<SessionResponse | null>(null);

  // Parsed once. `loadScript` never throws; it returns null and fills
  // `problems`, which go to the console for whoever has to fix the YAML.
  const [script] = useState<Script | null>(() => {
    const problems: string[] = [];
    const parsed = loadScript(scriptSource, problems);
    if (!parsed) console.error('[vrata.yaml] script rejected:\n' + problems.join('\n'));
    return parsed;
  });

  useEffect(() => {
    let cancelled = false;
    void getSession()
      .then((s) => !cancelled && setSession(s))
      .catch(() => {
        // The API is unreachable. The scene cannot ask "do you know me?", so
        // the honest move is the plain form, which will surface the real error
        // when they try to submit.
        if (!cancelled) setStage({ at: 'fallback', reason: 'server ne odgovara' });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toFallback = useCallback((reason: string) => setStage({ at: 'fallback', reason }), []);

  if (stage.at === 'fallback') {
    return <FallbackForm reason={stage.reason} onEntered={onEntered} />;
  }

  if (stage.at === 'door') {
    return (
      <Door
        // A returning member's door opens itself; a stranger has to knock.
        known={session?.greeting === 'sefe'}
        onOpen={() => {
          // The door tap is the user gesture that unlocks mobile audio.
          unlock();
          if (!script) {
            toFallback('scenario se ne učitava');
            return;
          }
          setStage({ at: 'scene' });
        }}
      />
    );
  }

  if (!script || !session) return null;

  return (
    <SceneBoundary onError={toFallback}>
      <BouncerScene
        script={script}
        session={session}
        onEntered={onEntered}
        onSceneError={toFallback}
      />
    </SceneBoundary>
  );
}

/**
 * Last line of defence. Design principle 4 says the joke is never
 * load-bearing; the door is the only way in, so a render error in the scene
 * has to become the plain form rather than a white screen.
 */
class SceneBoundary extends Component<
  { children: ReactNode; onError: (reason: string) => void },
  { crashed: boolean }
> {
  override state = { crashed: false };

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[door] scene crashed', error, info.componentStack);
    this.props.onError('scena je pukla');
  }

  override render() {
    return this.state.crashed ? null : this.props.children;
  }
}
